import makeStyles from '@mui/styles/makeStyles';
import {isCurriculumSessionStarted} from "business/curriculumSession";
import {orderSessionSteps} from "business/session";
import {isMentee, isMentor} from "business/role";
import {isOneToMany, isOneToOne} from "business/matching";
import {convertUrl} from "common/helpers";
import {history} from "common/history";
import {withPreviewQuery} from "common/preview";
import {color, font, radius, space, type} from "common/tokens";
import useFreshData, {dataKeys} from "common/useFreshData";
import {useSetTranslation} from "common/useSetTranslation";
import PreviewBanner from "components/PreviewBanner";
import Dialog from "components/redesign/Dialog";
import {AvatarGroup} from "components/redesign/Avatar";
import CtaButton from "components/redesign/CtaButton";
import ShimmerImage from "components/redesign/ShimmerImage";
import Skeleton from "components/redesign/Skeleton";
import classNames from "classnames";
import {Check} from "lucide-react";
import {find, get, isEmpty, map, some} from "lodash";
import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
  },
  back: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    color: color.TEXT_MUTED,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: '11px 0',
    cursor: 'pointer',
  },
  content: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
    padding: '34px 0 60px',
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: 0,
  },
  theme: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  banner: {
    background: color.PAPER,
    borderRadius: radius.PANEL,
    marginTop: 24.7,
  },
  bannerImage: {
    maxHeight: 297,
    objectFit: 'contain',
  },
  // Height is held even before the button is offered, so revealing it does not
  // shunt the step list down the page.
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    paddingTop: 16.69,
    minHeight: 48,
    boxSizing: 'content-box',
  },
  confirmBody: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  steps: {
    listStyle: 'none',
    margin: 0,
    padding: '25.5px 0 0',
    maxWidth: 720,
  },
  step: {
    appearance: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '20px 12px',
    border: 'none',
    borderBottom: `1px solid ${color.BORDER}`,
    borderRadius: radius.CONTROL,
    cursor: 'pointer',
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  stepNumber: {
    ...type.meta,
    width: 28,
    height: 28,
    flexShrink: 0,
    borderRadius: 14,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // A finished step trades its number for a tick, so progress down the list
  // reads at a glance rather than by counting.
  stepDone: {
    background: color.SUCCESS,
    color: color.SURFACE,
  },
  // The step to pick up next is filled rather than tinted, so the list says
  // where to resume without a label.
  stepCurrent: {
    background: color.ACCENT,
    color: color.ON_ACCENT,
  },
  stepBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  stepTitle: {
    ...type.cardHeading,
    letterSpacing: '-0.18px',
    color: color.TEXT,
    margin: 0,
  },
  stepDuration: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  // Who is on the step reads down the right edge of the list, away from the
  // name and the minutes.
  stepAvatars: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  // Mirrors the step row so the list does not shift as the real rows land.
  skeletonStep: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '20px 12px',
    borderBottom: `1px solid ${color.BORDER}`,
  },
  skeletonStepBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  skeletonBanner: {
    borderRadius: radius.PANEL,
  },
});

const SKELETON_STEPS = [0, 1, 2];

const fullName = person =>
  `${get(person, 'firstName', '')} ${get(person, 'lastName', '')}`.trim();

const MentorshipSessionSummary = ({
  match,
  isPreview,
  session,
  role,
  currentMentorshipSessionProgress,
  sessionProgress,
  mentorshipSessions,
  userDetails,
  mentorDetails,
  partnerMenteesDetails,
  mentorshipType,
  initSession,
  initMentorshipSessions
}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  // A session is opened from the milestones page as well as from the mentorship
  // home, and back belongs where the reader came from.
  const fromMilestones = get(history, 'location.state.from') === 'milestones';
  const backPath = fromMilestones ? '/mentorship/milestones' : '/mentorship';
  const backLabel = fromMilestones ? t('curriculumMilestones') : t('mentorship');
  const {st} = useSetTranslation();

  // Tracked by url rather than a boolean so a session change re-arms the
  // placeholder without a reset effect racing a cached image's onLoad.
  const [loadedBanner, setLoadedBanner] = useState(null);
  const [confirmingStart, setConfirmingStart] = useState(false);
  const banner = get(session, 'banner');
  const isBannerReady = !banner || loadedBanner === banner;

  const sessionIdFromRoute = get(match, 'params.sessionId');
  const sessionId = session && session.id ? session.id : sessionIdFromRoute;

  // Opening the page no longer starts the session: a session the group has not
  // begun can be previewed, and only Start writes the start to the server.
  useEffect(() => {
    if (!sessionIdFromRoute) return;
    initSession(sessionIdFromRoute, false);
  }, [sessionIdFromRoute]);

  const isComplete = (step, stepsStatus) =>
    some(stepsStatus, doneStep => step.id === doneStep.id && doneStep.status === "COMPLETED");
  const selfComplete = step => isComplete(step, get(currentMentorshipSessionProgress, 'steps'));
  const partnerStepDetails = (partners, partnerId) =>
    get(find(partners, partner => partner.userId === partnerId), 'stepDetails');
  const partnerComplete = (step, partnerId) =>
    isComplete(step, partnerStepDetails(get(currentMentorshipSessionProgress, 'partnerSteps'), partnerId));
  // The whole group on every step, marked done or not — showing only the people
  // who had opened a step hid the partners you are waiting on, which is the one
  // thing the row is there to tell you.
  const stepMembers = step => {
    const members = [];

    if (isMentee(role) && mentorDetails) {
      members.push({
        name: fullName(mentorDetails),
        image: get(mentorDetails, 'picture'),
        isDone: partnerComplete(step, mentorDetails.id),
      });
    }

    members.push({
      name: fullName(userDetails),
      label: t('you'),
      image: get(userDetails, 'picture'),
      isDone: selfComplete(step),
    });

    if (isOneToMany(mentorshipType) || (isOneToOne(mentorshipType) && isMentor(role))) {
      map(partnerMenteesDetails, mentee => {
        members.push({
          name: fullName(mentee),
          image: get(mentee, 'picture'),
          isDone: partnerComplete(step, mentee.id),
        });
      });
    }

    return members;
  };

  const sessionSteps = orderSessionSteps(session);
  const currentStepId = get(find(sessionSteps, step => !selfComplete(step)), 'id');
  const lastStepId = sid => get(sessionProgress, `${[sid]}`);
  const isSessionReady = Boolean(get(session, 'id'));
  const areStepsReady = !isEmpty(currentMentorshipSessionProgress);

  // The lists say whether the group has begun this session. They are the same
  // ones the home page reads, so this is cached rather than a second request.
  const sessionsSettled = useFreshData(dataKeys.MENTORSHIP_SESSIONS, initMentorshipSessions);
  const hasStarted =
    isCurriculumSessionStarted(mentorshipSessions, sessionId) || Boolean(lastStepId(sessionId));

  const stepsSkeleton = (
    <ul className={classes.steps}>
      {SKELETON_STEPS.map(index => (
        <li key={index}>
          <div className={classes.skeletonStep}>
            <Skeleton width={28} height={28} circle/>
            <span className={classes.skeletonStepBody}>
              <Skeleton width={'55%'} height={20}/>
              <Skeleton width={72} height={16}/>
            </span>
            <Skeleton width={30} height={30} circle/>
          </div>
        </li>
      ))}
    </ul>
  );

  // Walking into a session the group has not begun is a preview, not a start —
  // the same reading whether the reader used the Preview button or the list.
  const goStep = (stepId, preview = isPreview || !hasStarted) =>
    history.push(withPreviewQuery(`/mentorship/${sessionId}/step/${stepId}`, Boolean(preview)));

  const goNext = preview => {
    const nextStepId = lastStepId(sessionId) || get(sessionSteps, '[0].id');
    if (nextStepId) goStep(nextStepId, preview);
  };

  // The step page records the start when it is opened outside preview, so this
  // only has to take the reader in without the preview flag.
  const confirmStart = () => {
    setConfirmingStart(false);
    goNext(false);
  };

  // The whole page stands in for itself while the session loads, so the reader
  // sees the shape of what is coming rather than an empty screen filling in
  // piece by piece.
  if (!isSessionReady) {
    return (
      <div className={classes.page}>
        <div className={classes.container}>
          {isPreview && <PreviewBanner/>}

          <button type={'button'} className={classes.back} onClick={() => history.push(backPath)}>
            {`← ${backLabel}`}
          </button>

          <div className={classes.content}>
            <Skeleton width={'70%'} height={37}/>
            <Skeleton width={160} height={23}/>
            <Skeleton height={321} className={classes.skeletonBanner}/>
            <div className={classes.actions}>
              <Skeleton width={168} height={48}/>
            </div>
            {stepsSkeleton}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <div className={`${classes.container} ui-enter`}>
        {isPreview && <PreviewBanner/>}

        <button type={'button'} className={classes.back} onClick={() => history.push(backPath)}>
          {`← ${backLabel}`}
        </button>

        <div className={classes.content}>
          <h1 className={classes.title}>{st(session.title, session.localeTitle)}</h1>
          {Boolean(get(session, 'theme')) && <p className={classes.theme}>{get(session, 'theme')}</p>}

          {Boolean(banner) && (
            <ShimmerImage
              src={convertUrl(banner)}
              fit={'auto'}
              placeholderHeight={297}
              className={classes.banner}
              imageClassName={classes.bannerImage}
              onLoaded={() => setLoadedBanner(banner)}
            />
          )}

          {/* Held back until the banner is in place, so the page settles before
              it invites the reader to start. */}
          <div className={classes.actions}>
            {isBannerReady && sessionsSettled && (
              <>
                {/* Starting adds the group's timelines and begins recording, so
                    it is asked for rather than assumed. Until then the session
                    can be read through in preview. */}
                {!hasStarted && (
                  <CtaButton
                    size={'large'}
                    variant={'secondary'}
                    label={t('preview')}
                    onClick={() => goNext(true)}
                  />
                )}
                <CtaButton
                  size={'large'}
                  variant={'primary'}
                  label={hasStarted ? t('continueSession') : t('startSession')}
                  onClick={hasStarted ? () => goNext(false) : () => setConfirmingStart(true)}
                />
              </>
            )}
          </div>

          {!areStepsReady && stepsSkeleton}

          {areStepsReady && (
            <ul className={classes.steps}>
              {sessionSteps.map((step, index) => {
                const members = stepMembers(step);
                return (
                  <li key={step.id}>
                    <button type={'button'} className={classes.step} onClick={() => goStep(step.id)}>
                      <span
                        className={classNames(
                          classes.stepNumber,
                          selfComplete(step) && classes.stepDone,
                          !selfComplete(step) && step.id === currentStepId && classes.stepCurrent
                        )}
                      >
                        {selfComplete(step) ? <Check size={15} strokeWidth={3}/> : index + 1}
                      </span>
                      <span className={classes.stepBody}>
                        <span className={classes.stepTitle}>{st(step.title, step.localeTitle)}</span>
                        {Boolean(step.duration) && <span className={classes.stepDuration}>{step.duration}</span>}
                      </span>
                      {Boolean(members.length) && (
                        <span className={classes.stepAvatars}>
                          <AvatarGroup members={members} variant={'step'} ring={false} dimWhenPending/>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {confirmingStart && (
        <Dialog
          title={t('startConfirmTitle')}
          maxWidth={460}
          onClose={() => setConfirmingStart(false)}
          footer={
            <>
              <CtaButton
                variant={'secondary'}
                label={t('no')}
                onClick={() => setConfirmingStart(false)}
              />
              <CtaButton variant={'primary'} label={t('yes')} onClick={confirmStart}/>
            </>
          }
        >
          <p className={classes.confirmBody}>{t('startConfirmBody')}</p>
        </Dialog>
      )}
    </div>
  );
};

export default MentorshipSessionSummary;
